import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CreateUserRequest,
  DeleteUserRequest,
  EditUserRequest,
  EditUserResponse,
  GetUsersResponse,
  adminResetPassword,
  createUser,
  deleteUser,
  editUser,
  getUsers,
} from "../api/user";
import User from "../types/user/User";

export const usersQueryKey = ["users"];

export const useUsers = (
  customerId: string | undefined,
  ready: boolean = true
) => {
  return useQuery<GetUsersResponse, Error>({
    queryKey: usersQueryKey,
    queryFn: () => getUsers(customerId!),
    enabled: !!customerId && ready,
  });
};

export const useCreateUser = (customerId: string) => {
  const queryClient = useQueryClient();
  const mutationOptions: UseMutationOptions<
    User | undefined,
    Error,
    CreateUserRequest
  > = {
    mutationFn: (request: CreateUserRequest) => {
      return createUser(customerId, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  };
  return useMutation(mutationOptions);
};

export const useEditUser = (customerId: string) => {
  const queryClient = useQueryClient();
  const mutationOptions: UseMutationOptions<
    EditUserResponse,
    Error,
    EditUserRequest
  > = {
    mutationFn: (request: EditUserRequest) => {
      return editUser(customerId, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  };
  return useMutation(mutationOptions);
};

export const useDeleteUser = (customerId: string) => {
  const queryClient = useQueryClient();
  const mutationOptions: UseMutationOptions<
    void | undefined,
    Error,
    DeleteUserRequest
  > = {
    mutationFn: (request: DeleteUserRequest) => {
      return deleteUser(customerId, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  };
  return useMutation(mutationOptions);
};

export function useAdminResetPassword() {
  return useMutation({
    mutationFn: adminResetPassword,
  });
}
